import {
  Component,
  OnInit,
  AfterViewInit,
  signal,
  computed,
  effect,
  ViewChild,
  ElementRef
} from '@angular/core';
import { NgIf, NgFor, NgClass, DecimalPipe } from '@angular/common';
import * as L from 'leaflet';

import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { DataService, FlatRow, EncounterRow } from '../../data.service';

Chart.register(...registerables);

const OTTAWA_CENTER_LAT = 45.4215;
const OTTAWA_CENTER_LNG = -75.6972;

// --------------------- helpers ---------------------------------------------

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function parseLocalDateInput(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d); // local midnight
}

function daysBetweenStr(start: string | null, end: string | null): number | null {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return null;
  const diffMs = e.getTime() - s.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function median(values: number[]): number | null {
  const filtered = values.filter(v => Number.isFinite(v));
  const n = filtered.length;
  if (!n) return null;
  const sorted = [...filtered].sort((a, b) => a - b);
  const mid = Math.floor(n / 2);
  return n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function monthKey(dateStr: string | null): string | null {
  const d = parseDate(dateStr);
  if (!d) return null;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const mm = m.toString().padStart(2, '0');
  return `${y}-${mm}`;
}

function monthLabel(key: string): string {
  const [yStr, mStr] = key.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[m - 1]} ${y}`;
}

function monthKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${y}-${m}`;
}

function comorbBand(count: number | null): '0' | '1-2' | '3-4' | '5+' | 'unknown' {
  if (count == null) return 'unknown';
  if (count === 0) return '0';
  if (count <= 2) return '1-2';
  if (count <= 4) return '3-4';
  return '5+';
}

function ageBandFromDob(dobStr: string | null, ref: Date): string | null {
  const d = parseDate(dobStr);
  if (!d) return null;
  const ageYears = (ref.getTime() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  if (ageYears < 50) return '<50';
  if (ageYears < 65) return '50-64';
  if (ageYears < 75) return '65-74';
  return '75+';
}

// map centroids – same logic as before
function pseudoFsaCentroid(fsa: string): { lat: number; lng: number } {
  const clean = (fsa || '').trim().toUpperCase();

  // Hardcoded centroids (approx) for Ontario FSAs in the UOHI catchment.
  // Privacy-safe: these are broad area centroids, not full postal codes.
  const FSA_CENTROIDS: Record<string, { lat: number; lng: number }> = {
    // Ottawa (K1*, K2*)
    K1A: { lat: 45.4215, lng: -75.6972 },
    K1B: { lat: 45.4215, lng: -75.6200 },
    K1C: { lat: 45.4500, lng: -75.5200 },
    K1G: { lat: 45.3900, lng: -75.6600 },
    K1H: { lat: 45.3600, lng: -75.7000 },
    K1J: { lat: 45.4600, lng: -75.6400 },
    K1K: { lat: 45.4400, lng: -75.6500 },
    K1L: { lat: 45.4500, lng: -75.6900 },
    K1N: { lat: 45.4300, lng: -75.6900 },
    K1P: { lat: 45.4200, lng: -75.7000 },
    K1R: { lat: 45.4200, lng: -75.7200 },
    K1S: { lat: 45.3900, lng: -75.6900 },
    K1T: { lat: 45.3500, lng: -75.6200 },
    K1V: { lat: 45.3300, lng: -75.6500 },
    K1W: { lat: 45.2900, lng: -75.5700 },

    K2A: { lat: 45.3800, lng: -75.7700 },
    K2B: { lat: 45.3600, lng: -75.8000 },
    K2C: { lat: 45.3100, lng: -75.7900 },
    K2E: { lat: 45.3300, lng: -75.7300 },
    K2G: { lat: 45.3500, lng: -75.7200 },
    K2H: { lat: 45.3400, lng: -75.9000 },
    K2J: { lat: 45.2800, lng: -75.7400 },
    K2K: { lat: 45.4700, lng: -75.5200 },
    K2L: { lat: 45.3500, lng: -75.9100 },
    K2M: { lat: 45.2600, lng: -75.7600 },
    K2P: { lat: 45.2500, lng: -75.6900 },
    K2R: { lat: 45.3100, lng: -75.6200 },

    // Kemptville / North Grenville (K0G*)
    K0G: { lat: 45.0167, lng: -75.6500 },

    // Perth / Smiths Falls / Lanark (K7H*)
    K7H: { lat: 44.9000, lng: -76.2500 },

    // Cornwall (K6H*, K6J*)
    K6H: { lat: 45.0180, lng: -74.7280 },
    K6J: { lat: 45.0000, lng: -74.7200 },

    // Hawkesbury (K6A*)
    K6A: { lat: 45.6000, lng: -74.6000 },

    // Barry's Bay / Renfrew County rural (K0J*)
    K0J: { lat: 45.4900, lng: -77.6800 },

    // Pembroke (optional, if you later include it)
    K8A: { lat: 45.8260, lng: -77.1100 }
  };

  // ---- Chart.js global typography tuning (TypeScript-safe) ----
Chart.defaults.font = {
  family: 'Roboto, sans-serif',
  size: 11
};

Chart.defaults.color = '#9aa0a6';

// Legend fonts
Chart.defaults.plugins.legend.labels.font = { size: 11 };

// Tooltip fonts
Chart.defaults.plugins.tooltip.titleFont = { size: 11 };
Chart.defaults.plugins.tooltip.bodyFont = { size: 10 };

  // Try direct lookup
  if (clean.length >= 3) {
    const key = clean.slice(0, 3);
    if (FSA_CENTROIDS[key]) return FSA_CENTROIDS[key];

    // Fallback: group by first two characters (e.g., K1, K2, K6...)
    const prefix2 = key.slice(0, 2);
    if (prefix2 === 'K1' || prefix2 === 'K2') return { lat: 45.4215, lng: -75.6972 }; // Ottawa
    if (prefix2 === 'K6') return { lat: 45.2000, lng: -74.7000 }; // Cornwall/Hawkesbury region
    if (prefix2 === 'K7') return { lat: 44.9000, lng: -76.2500 }; // Perth area
    if (prefix2 === 'K0') return { lat: 45.0167, lng: -75.6500 }; // rural catchment baseline
  }

  // Last resort: Ottawa center
  return { lat: 45.4215, lng: -75.6972 };
}

// normalize fields coming from MASTER
function normStatus(s: string | null | undefined): 'on_waitlist' | 'scheduled' | 'completed' | 'unknown' {
  const v = (s || '').toString().trim().toLowerCase();
  if (v === 'on waitlist' || v === 'waitlist') return 'on_waitlist';
  if (v === 'scheduled') return 'scheduled';
  if (v === 'completed') return 'completed';
  return 'unknown';
}

function normPriority(p: string | null | undefined): 'urgent' | 'priority' | 'elective' | 'unknown' {
  const v = (p || '').toString().trim().toLowerCase();
  if (v === 'urgent') return 'urgent';
  if (v === 'priority') return 'priority';
  if (v === 'elective') return 'elective';
  return 'unknown';
}

// ---------------------------------------------------------------------------

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, DecimalPipe],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class Dashboard implements OnInit, AfterViewInit {
  // ----- base data & state -------------------------------------------------
  readonly rows = signal<FlatRow[]>([]);
  readonly loading = signal<boolean>(true);
  readonly error = signal<string | null>(null);
  readonly encounters = signal<EncounterRow[]>([]);
  readonly schedSort = signal<string>('priority');
  readonly schedSortDir = signal<'asc' | 'desc'>('desc');
  readonly viewMode = signal<'map' | 'descriptive' | 'schedule'>('map');
  readonly daysBetweenStr = daysBetweenStr;
  readonly normStatus = normStatus;
  readonly normPriority = normPriority;

// Worklist header sort (clickable columns)
readonly worklistSortKey = signal<
  | 'patient' | 'age' | 'sex' | 'status' | 'priority' | 'physician'
  | 'referral' | 'scheduled' | 'days_waiting' | 'cha2' | 'comorb' | 'ed' | 'acute'
  | null
>(null);

readonly worklistSortDir = signal<'asc' | 'desc'>('asc');

toggleWorklistSort(
  key:
    | 'patient' | 'age' | 'sex' | 'status' | 'priority' | 'physician'
    | 'referral' | 'scheduled' | 'days_waiting' | 'cha2' | 'comorb' | 'ed' | 'acute'
): void {
  const curKey = this.worklistSortKey();
  const curDir = this.worklistSortDir();

  // Not sorted yet OR switching to a new column: start with ASC
  if (curKey !== key) {
    this.worklistSortKey.set(key);
    this.worklistSortDir.set('asc');
    return;
  }

  // Same column: ASC -> DESC -> UNSORTED
  if (curDir === 'asc') {
    this.worklistSortDir.set('desc');
    return;
  }

  // curDir === 'desc' -> clear sort
  this.worklistSortKey.set(null);
  this.worklistSortDir.set('asc'); // reset to default for next time
}

  // ---------------- helper: robust field lookup ----------------
  private getFieldInsensitive(r: any, wanted: string): any {
    if (!r) return null;

    // direct hit first
    if (wanted in r) return r[wanted];

    const norm = (s: string) =>
      s
        .toString()
        .replace(/\uFEFF/g, '') // strip BOM if present
        .trim()
        .toLowerCase();

    const target = norm(wanted);

    for (const k of Object.keys(r)) {
      if (norm(k) === target) return (r as any)[k];
    }
    return null;
  }

  // MAP FILTERS
  readonly priorityFilter = signal<'all' | 'urgent' | 'priority' | 'elective'>('all');
  readonly ageBandFilter = signal<'all' | '<50' | '50-64' | '65-74' | '75+'>('all');
  readonly sexFilter = signal<'all' | 'M' | 'F' | 'Other'>('all');
  readonly fsaFilter = signal<'all' | string>('all');

  // DESCRIPTIVE FILTERS (for line chart slices)
  readonly descPhysicianFilter = signal<'all' | string>('all');
  readonly descDxFilter = signal<'all' | string>('all');
  readonly descComorbFilter = signal<'all' | '0' | '1-2' | '3-4' | '5+' | 'unknown'>('all');

  // Demand vs Capacity date filter (for this chart only, for now)
  readonly demandStartDate = signal<string>('2025-01-01');
  readonly demandEndDate   = signal<string>('2025-12-31');

getScheduledDate(r: FlatRow): string | null {
  const raw = this.getFieldInsensitive(r as any, 'Scheduled_Date');
  const d = parseDate(raw ?? null);
  return d ? d.toISOString().slice(0, 10) : null;
}

getReferralDate(r: FlatRow): string | null {
  const d = parseDate(r.Referral_Date ?? null);
  return d ? d.toISOString().slice(0, 10) : null;
}

getWait1Days(r: FlatRow): number | null {
  const v = (r as any).Wait1_Days;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

getWait2Days(r: FlatRow): number | null {
  const v = (r as any).Wait2_Days;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

getCha2(r: FlatRow): number | null {
  const v = (r as any).CHA2DS2_VASc_Score;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

getEdVisits(r: FlatRow): number | null {
  const v = (r as any).ED_Visits_AFib_2025;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

getAcuteCases(r: FlatRow): number | null {
  const v = (r as any).Acute_Care_Cases_2025;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

getStatusLabel(r: FlatRow): string {
  const s = normStatus(r.Current_Status);
  return s === 'on_waitlist' ? 'On waitlist' : s === 'scheduled' ? 'Scheduled' : (r.Current_Status || '—');
}

getPriorityLabel(r: FlatRow): string {
  const p = normPriority(r.Priority_Level);
  return p === 'urgent' ? 'Urgent' : p === 'priority' ? 'Priority' : p === 'elective' ? 'Elective' : (r.Priority_Level || '—');
}

getRowClass(r: FlatRow): string {
  const p = normPriority(r.Priority_Level);
  const s = normStatus(r.Current_Status);
  return `pri-${p} status-${s}`;
}

getDaysWaiting(r: FlatRow): number | null {
  const todayIso = new Date().toISOString();
  return daysBetweenStr(r.Referral_Date ?? null, todayIso);
}

setSchedSort(v: string): void {
  this.schedSort.set(v || 'priority');
}

setSchedSortDir(v: string): void {
  const x = (v || 'desc').toLowerCase();
  this.schedSortDir.set(x === 'asc' ? 'asc' : 'desc');
}


  setDemandStartDate(v: string): void {
    this.demandStartDate.set(v);
  }

  setDemandEndDate(v: string): void {
    this.demandEndDate.set(v);
}

getAge(r: FlatRow): number | null {

  // 🔍 TEMP DEBUG — remove after
  if ((r as any).Patient_ID === 'P0162') {
    console.log('ROW KEYS:', Object.keys(r as any));
    console.log('AGE RAW:', this.getFieldInsensitive(r as any, 'Age_At_2025'));
    console.log('SCHED RAW:', this.getFieldInsensitive(r as any, 'Scheduled_Date'));
  }

  const raw =
    this.getFieldInsensitive(r as any, 'Age_At_2025') ??
    this.getFieldInsensitive(r as any, 'Age');

  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;

  const s = (raw ?? '').toString().trim();
  if (!s) return null;

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

getSortIcon(key: string): string {
  if (this.worklistSortKey() !== key) {
    return '⇅'; // unsorted hint
  }
  return this.worklistSortDir() === 'asc' ? '▲' : '▼';
}

getComorbCount(r: FlatRow): number | null {
  // Try common header variants
  const raw =
    (r as any).Comorbidity_Count ??
    (r as any).Comorb_Count ??
    (r as any).Comorbidities_Count ??
    (r as any).comorbidity_count;

  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;

  const s = (raw ?? '').toString().trim();
  if (!s) return null;

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

  // Options for filters
  readonly fsaOptions = computed<string[]>(() => {
    const fsas = new Set<string>();
    this.rows().forEach(r => {
      const fsa = (r.Postal_Code || '').slice(0, 3).toUpperCase();
      if (fsa) fsas.add(fsa);
    });
    return Array.from(fsas).sort();
  });

  readonly physicianOptions = computed<string[]>(() => {
    const set = new Set<string>();
    this.rows().forEach(r => {
      const v = (r.Assigned_Physician || '').trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  });

  readonly diagnosisOptions = computed<string[]>(() => {
    const set = new Set<string>();
    this.rows().forEach(r => {
      const v = (r.MRDx_ICD10CA_Display || '').trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  });

  // Filter rows for MAP (postal centroids)
  readonly filteredRows = computed<FlatRow[]>(() => {
    const rows = this.rows();
    if (!rows.length) return [];

    const pri = this.priorityFilter();
    const ageBandChoice = this.ageBandFilter();
    const sexChoice = this.sexFilter();
    const fsaChoice = this.fsaFilter();
    const today = new Date();

    return rows.filter(r => {
      if (pri !== 'all') {
        const p = normPriority(r.Priority_Level);
        if (p !== pri) return false;
      }

      if (ageBandChoice !== 'all') {
        const band = ageBandFromDob(r.DOB ?? null, today);
        if (band !== ageBandChoice) return false;
      }

      if (sexChoice !== 'all') {
        const sx = (r.Sex || '').toString().toUpperCase();
        if (sx !== sexChoice) return false;
      }

      if (fsaChoice !== 'all') {
        const fsa = (r.Postal_Code || '').slice(0, 3).toUpperCase();
        if (fsa !== fsaChoice) return false;
      }

      return true;
    });
  });

// Patient IDs in scope for the selected Physician filter (used for encounter-based trends)
readonly physCohortPatientIds = computed(() => {
  const rows = this.rows();
  const phys = this.descPhysicianFilter();

  const ids = new Set<string>();
  for (const r of rows) {
    if (phys !== 'all') {
      const p = (r.Assigned_Physician || '').trim();
      if (p !== phys) continue;
    }
    if (r.Patient_ID) ids.add(r.Patient_ID);
  }
  return ids;
});

// ED sparkline (REAL monthly ED visits due to AFib, filtered by encounter Start_Date)
readonly edSpark = computed(() => {
  const enc = this.encounters();
  const patientIds = this.physCohortPatientIds();

  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  // Month axis
  const months: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    months.push(monthKeyFromDate(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const counts = new Map<string, number>();
  for (const k of months) counts.set(k, 0);

  let total = 0;

  for (const e of enc) {
    if (!e.Patient_ID || !patientIds.has(e.Patient_ID)) continue;

    const isED = (e.Encounter_Type || '').toLowerCase() === 'ed';
    const isAfib =
      (e.Encounter_Dx_Group || '').toLowerCase().includes('atrial fibrillation') ||
      (e.Encounter_ICD10CA_Code || '').toUpperCase().startsWith('I48');

    if (!isED || !isAfib) continue;

    const dt = parseDate(e.Start_Date);
    if (!dt || dt < start || dt > end) continue;

    const mk = monthKey(e.Start_Date);
    if (!mk || !counts.has(mk)) continue;

    counts.set(mk, (counts.get(mk) ?? 0) + 1);
    total += 1;
  }

  const values = months.map(k => counts.get(k) ?? 0);
  const labels = months.map(monthLabel);

  return { labels, values, total };
});

// Acute sparkline (REAL monthly acute admissions, filtered by encounter Start_Date)
readonly acuteSpark = computed(() => {
  const enc = this.encounters();
  const patientIds = this.physCohortPatientIds();

  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  // Month axis
  const months: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    months.push(monthKeyFromDate(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const counts = new Map<string, number>();
  for (const k of months) counts.set(k, 0);

  let total = 0;
  const admittedPatients = new Set<string>();

  for (const e of enc) {
    if (!e.Patient_ID || !patientIds.has(e.Patient_ID)) continue;

    const isAcute = (e.Encounter_Type || '').toLowerCase() === 'acute care';
    if (!isAcute) continue;

    const dt = parseDate(e.Start_Date);
    if (!dt || dt < start || dt > end) continue;

    const mk = monthKey(e.Start_Date);
    if (!mk || !counts.has(mk)) continue;

    counts.set(mk, (counts.get(mk) ?? 0) + 1);
    total += 1;
    admittedPatients.add(e.Patient_ID);
  }

  const cohortPatients = patientIds.size || 1;
  const rate = (admittedPatients.size / cohortPatients) * 100;

  const values = months.map(k => counts.get(k) ?? 0);
  const labels = months.map(monthLabel);

  return {
    labels,
    values,
    total,
    patientsAdmitted: admittedPatients.size,
    rate: Number(rate.toFixed(1))
  };
});

readonly qolDist = computed(() => {
  const cohort = this.demandCohortRows();

  // Unique patient QoL change
  const byPatient = new Map<string, number>();
  for (const r of cohort) {
    const pid = r.Patient_ID;
    if (!pid) continue;
    const d = Number((r as any).QoL_Change ?? NaN);
    if (Number.isFinite(d)) byPatient.set(pid, d);
  }

  const vals = Array.from(byPatient.values());
  const buckets = {
    negOrZero: vals.filter(v => v <= 0).length,
    oneToTen: vals.filter(v => v > 0 && v <= 10).length,
    elevenToTwenty: vals.filter(v => v > 10 && v <= 20).length,
    overTwenty: vals.filter(v => v > 20).length
  };
  const max = Math.max(1, ...Object.values(buckets));
  return {
    ...buckets,
    max
  };
});

readonly losGauge = computed(() => {
  const l = this.losKpi();
  if (!l) return { pct: 0, targetPct: 50 }; // default

  // Gauge range 0–10 days for avg LOS (cap at 10)
  const avg = l.avgLos ?? 0;
  const pct = Math.max(0, Math.min(100, (avg / 10) * 100));

  // Target marker at 5 days (50%)
  const targetPct = (5 / 10) * 100;

  return { pct, targetPct };
});

  // -------------------------------------------------------------------------
  // ✅ LEFT 3 tiles: Wait 1 / Wait 2 / Total Wait
  // -------------------------------------------------------------------------
  readonly descriptiveSummary = computed(() => {
    const rows = this.demandCohortRows();
    if (!rows.length) {
      return {
        avgWait1: null as number | null,
        avgWait2: null as number | null,
        avgTotalWait: null as number | null
      };
    }

    const today = new Date();
    const wait1: number[] = [];
    const wait2Completed: number[] = [];
    const totalWait: number[] = [];

    const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

    for (const r of rows) {
      // Wait1: use Master-derived field
      const w1 = (r as any).Wait1_Days as number | null | undefined;
      if (w1 != null && Number.isFinite(w1) && w1 >= 0) wait1.push(w1);

      // Wait2: completed only
      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');

      const w2 = (r as any).Wait2_Days as number | null | undefined;
      if (isCompleted && w2 != null && Number.isFinite(w2) && w2 >= 0) wait2Completed.push(w2);

      // Total wait: referral -> procedure if completed, else referral -> today
      const endStr = (isCompleted && r.Procedure_Date) ? (r.Procedure_Date as string) : today.toISOString();
      const tw = daysBetweenStr(r.Referral_Date ?? null, endStr);
      if (tw != null && tw >= 0) totalWait.push(tw);
    }

    return {
      avgWait1: avg(wait1),
      avgWait2: avg(wait2Completed),
      avgTotalWait: avg(totalWait)
    };
  });


  
// Cohort for right-panel metrics (same filters as Demand vs Capacity)
// Physician + Start/End, cohort defined by Referral_Date
readonly demandCohortRows = computed(() => {
  const rows = this.rows();
  const physFilter = this.descPhysicianFilter();

  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  return rows.filter(r => {
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) return false;
    }
    const rdt = parseDate(r.Referral_Date ?? null);
    if (!rdt || rdt < start || rdt > end) return false;
    return true;
  });
});

// 1) Quality of Life (AFEQT): average baseline, follow-up, delta
readonly qolKpi = computed(() => {
  const cohort = this.demandCohortRows();
  if (!cohort.length) return { baseline: null as number | null, follow: null as number | null, delta: null as number | null };

  // Use unique patients (avoid double-counting if patient has >1 referral)
  const byPatient = new Map<string, { b?: number; f?: number }>();

  for (const r of cohort) {
    const pid = r.Patient_ID;
    if (!pid) continue;

    const b = (r as any).QoL_Baseline_AFEQT as number | null | undefined;
    const f = (r as any).QoL_FollowUp_AFEQT as number | null | undefined;

    if (!byPatient.has(pid)) byPatient.set(pid, {});
    const obj = byPatient.get(pid)!;

    if (typeof b === 'number' && Number.isFinite(b)) obj.b = b;
    if (typeof f === 'number' && Number.isFinite(f)) obj.f = f;
  }

  const baselines: number[] = [];
  const follows: number[] = [];
  const deltas: number[] = [];

  for (const v of byPatient.values()) {
    if (typeof v.b === 'number') baselines.push(v.b);
    if (typeof v.f === 'number') follows.push(v.f);
    if (typeof v.b === 'number' && typeof v.f === 'number') deltas.push(v.f - v.b);
  }

  const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

  const baseline = avg(baselines);
  const follow = avg(follows);
  const delta = avg(deltas);

  return {
    baseline: baseline != null ? Number(baseline.toFixed(1)) : null,
    follow: follow != null ? Number(follow.toFixed(1)) : null,
    delta: delta != null ? Number(delta.toFixed(1)) : null
  };
});

// 2) # ED visits due to AFib (sum across unique patients in cohort)
readonly edKpi = computed(() => {
  const cohort = this.demandCohortRows();
  const byPatient = new Map<string, number>();

  for (const r of cohort) {
    const pid = r.Patient_ID;
    if (!pid) continue;
    const ed = Number((r as any).ED_Visits_AFib_2025 ?? 0);
    if (!byPatient.has(pid)) byPatient.set(pid, 0);
    byPatient.set(pid, Math.max(byPatient.get(pid)!, Number.isFinite(ed) ? ed : 0));
  }

  const total = Array.from(byPatient.values()).reduce((a, b) => a + b, 0);
  return { total };
});

// 3) # Acute Care cases (sum across unique patients in cohort)
readonly acuteKpi = computed(() => {
  const cohort = this.demandCohortRows();
  const byPatient = new Map<string, number>();

  for (const r of cohort) {
    const pid = r.Patient_ID;
    if (!pid) continue;
    const ac = Number((r as any).Acute_Care_Cases_2025 ?? 0);
    if (!byPatient.has(pid)) byPatient.set(pid, 0);
    byPatient.set(pid, Math.max(byPatient.get(pid)!, Number.isFinite(ac) ? ac : 0));
  }

  const total = Array.from(byPatient.values()).reduce((a, b) => a + b, 0);
  return { total };
});

// 4) LOS (total bed-days) + average LOS per acute admission
readonly losKpi = computed(() => {
  const cohort = this.demandCohortRows();
  const losByPatient = new Map<string, number>();
  const acByPatient = new Map<string, number>();

  for (const r of cohort) {
    const pid = r.Patient_ID;
    if (!pid) continue;

    const los = Number((r as any).LOS_Admitted_Days_2025 ?? 0);
    const ac = Number((r as any).Acute_Care_Cases_2025 ?? 0);

    if (!losByPatient.has(pid)) losByPatient.set(pid, 0);
    if (!acByPatient.has(pid)) acByPatient.set(pid, 0);

    losByPatient.set(pid, Math.max(losByPatient.get(pid)!, Number.isFinite(los) ? los : 0));
    acByPatient.set(pid, Math.max(acByPatient.get(pid)!, Number.isFinite(ac) ? ac : 0));
  }

  const totalLos = Array.from(losByPatient.values()).reduce((a, b) => a + b, 0);
  const totalAc = Array.from(acByPatient.values()).reduce((a, b) => a + b, 0);

  const avgLos = totalAc > 0 ? totalLos / totalAc : null;

  return {
    totalLos,
    avgLos: avgLos != null ? Number(avgLos.toFixed(1)) : null,
    totalAc
  };
});

  // -------------------------------------------------------------------------
  // ✅ RIGHT 3 tiles: Completed / On Waitlist / Not Scheduled
  // -------------------------------------------------------------------------
  readonly waitlistStatusSummary = computed(() => {
    const rows = this.demandCohortRows();

    let completed = 0;
    let onWaitlist = 0;
    let scheduled = 0;

    for (const r of rows) {
      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');

      if (isCompleted) completed++;
      else if (status === 'scheduled') scheduled++;
      else if (status === 'on_waitlist') onWaitlist++;
    }

    return {
      // keep backward compatibility if anything else still references waiting
      waiting: onWaitlist,
      completed,
      scheduled,
      onWaitlist,
      notScheduled: rows.length - completed - scheduled
    };
  });

  // ----- line chart data (demand vs capacity over time, with slices) -------
  readonly lineChartData = computed(() => {
  const rows = this.rows();
  if (!rows.length) return { labels: [] as string[], referrals: [] as number[], procedures: [] as number[] };

  const physFilter = this.descPhysicianFilter();

  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end   = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);

  // --- show one month of context before start (display only) ---
const showContextMonth =
  start.getFullYear() !== 2025 || start.getMonth() !== 0;

  const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
  const contextMonth = new Date(startMonth);
  contextMonth.setMonth(contextMonth.getMonth() - 1);

  // normalize end to end-of-day
  end.setHours(23, 59, 59, 999);

  const refCounts = new Map<string, number>();
  const procCounts = new Map<string, number>();

  for (const r of rows) {
    // keep physician filter only
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) continue;
    }

    // Demand = referrals by Referral_Date
    const rdt = parseDate(r.Referral_Date ?? null);
    if (rdt && rdt >= start && rdt <= end) {
      const refKey = monthKey(r.Referral_Date ?? null);
      if (refKey) refCounts.set(refKey, (refCounts.get(refKey) ?? 0) + 1);
    }

    // Capacity = completed procedures by Procedure_Date
    const status = normStatus(r.Current_Status);
    const outcome = (r.Outcome || '').toString().toLowerCase();
    const isCompleted = status === 'completed' || outcome.includes('completed');

    if (isCompleted && r.Procedure_Date) {
      const pdt = parseDate(r.Procedure_Date ?? null);
      if (pdt && pdt >= start && pdt <= end) {
        const procKey = monthKey(r.Procedure_Date ?? null);
        if (procKey) procCounts.set(procKey, (procCounts.get(procKey) ?? 0) + 1);
      }
    }
  }

  // Build a continuous month axis between start and end (plus 1 context month)
  const months: string[] = [];
  const cursor = new Date(showContextMonth ? contextMonth : startMonth);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cursor <= endMonth) {
  const y = cursor.getFullYear();
  const m = (cursor.getMonth() + 1).toString().padStart(2, '0');
  months.push(`${y}-${m}`);
  cursor.setMonth(cursor.getMonth() + 1);
}

if (showContextMonth) {
  const ctxStart = new Date(contextMonth.getFullYear(), contextMonth.getMonth(), 1);
  const ctxEnd = new Date(contextMonth.getFullYear(), contextMonth.getMonth() + 1, 0);
  ctxEnd.setHours(23, 59, 59, 999);

  for (const r of rows) {
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) continue;
    }

    // context demand
    const rdt = parseDate(r.Referral_Date ?? null);
    if (rdt && rdt >= ctxStart && rdt <= ctxEnd) {
      const k = monthKey(r.Referral_Date ?? null);
      if (k) refCounts.set(k, (refCounts.get(k) ?? 0) + 1);
    }

    // context capacity (completed only)
    const status = normStatus(r.Current_Status);
    const outcome = (r.Outcome || '').toString().toLowerCase();
    const isCompleted = status === 'completed' || outcome.includes('completed');

    if (isCompleted && r.Procedure_Date) {
      const pdt = parseDate(r.Procedure_Date ?? null);
      if (pdt && pdt >= ctxStart && pdt <= ctxEnd) {
        const k = monthKey(r.Procedure_Date ?? null);
        if (k) procCounts.set(k, (procCounts.get(k) ?? 0) + 1);
      }
    }
  }
}
  const labels = months.map(monthLabel);
  const referrals = months.map(k => refCounts.get(k) ?? 0);
  const procedures = months.map(k => procCounts.get(k) ?? 0);

  return { labels, referrals, procedures };
});

 // Average wait by priority (all episodes) — includes waiting "to-date"
// Respects Demand vs Capacity filters: Physician + Start/End date
readonly waitByPriorityData = computed(() => {
  const rows = this.rows();
  if (!rows.length) return { labels: [] as string[], values: [] as number[] };

  const physFilter = this.descPhysicianFilter();
  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  const today = new Date();

  const byPriority = new Map<string, number[]>();

  for (const r of rows) {
    // Physician filter
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) continue;
    }

    const priLabel = (r.Priority_Level || 'Unknown').toString().trim() || 'Unknown';

    // Determine end-of-episode date:
    // - Completed: use Procedure_Date
    // - Otherwise: use "today" (wait-to-date), consistent with Avg Total Wait KPI
    const status = normStatus(r.Current_Status);
    const outcome = (r.Outcome || '').toString().toLowerCase();
    const isCompleted = status === 'completed' || outcome.includes('completed');

    const endDateStr = (isCompleted && r.Procedure_Date) ? (r.Procedure_Date as string) : today.toISOString();

    // Apply the date window consistently:
    // Use Referral_Date as the episode "inclusion" date (cohort definition),
    // so waiting patients still appear when filtering.
    const rdt = parseDate(r.Referral_Date ?? null);
    if (!rdt || rdt < start || rdt > end) continue;

    // Wait definition: Referral -> (Procedure or Today)
    const d = daysBetweenStr(r.Referral_Date ?? null, endDateStr);
    if (d == null || d < 0) continue;

    if (!byPriority.has(priLabel)) byPriority.set(priLabel, []);
    byPriority.get(priLabel)!.push(d);
  }

// Sort priority order: Elective → Priority → Urgent
const priorityOrder = ['Elective', 'Priority', 'Urgent'];
const labels = priorityOrder.filter(p => byPriority.has(p));

  const avg = (arr: number[]) => (arr.length ? arr.reduce((x, y) => x + y, 0) / arr.length : 0);
  const values = labels.map(l => avg(byPriority.get(l)!) );

  return { labels, values };
});

  // Current waitlist patients by physician & priority (NOT completed)
// Respects Demand vs Capacity filters: Physician + Start/End date (by Referral_Date)
readonly backlogByPhysPriorityData = computed(() => {
  const rows = this.rows();
  if (!rows.length) {
    return { physicians: [] as string[], priorities: [] as string[], matrix: [] as number[][] };
  }

  const physFilter = this.descPhysicianFilter();
  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  // Keep priority order consistent everywhere
  const priorities = ['Elective', 'Priority', 'Urgent'];

  // Filter to “current waitlist patients” in the selected window
  const active = rows.filter(r => {
    // apply Physician filter (if a single physician selected)
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) return false;
    }

    // window inclusion: use Referral_Date so waiting patients still count
    const rdt = parseDate(r.Referral_Date ?? null);
    if (!rdt || rdt < start || rdt > end) return false;

    // current waitlist = NOT completed
    const s = normStatus(r.Current_Status);
    return s === 'on_waitlist';
  });

  // Determine physicians in scope
  const physSet = new Set<string>();
  active.forEach(r => {
    const p = ((r.Assigned_Physician || 'Unknown').toString().trim()) || 'Unknown';
    physSet.add(p);
  });

  const physicians = Array.from(physSet).sort();

  // Matrix: rows = priorities, cols = physicians
  const matrix: number[][] = priorities.map(() => physicians.map(() => 0));

  for (const r of active) {
    const p = ((r.Assigned_Physician || 'Unknown').toString().trim()) || 'Unknown';
    const pri = ((r.Priority_Level || 'Unknown').toString().trim()) || 'Unknown';

    const pIdx = physicians.indexOf(p);
    const priIdx = priorities.indexOf(pri);

    if (pIdx >= 0 && priIdx >= 0) matrix[priIdx][pIdx] += 1;
  }

  return { physicians, priorities, matrix };
});

 // Age & sex distribution — respects Demand vs Capacity filters
readonly ageSexData = computed(() => {
  const rows = this.rows();
  if (!rows.length) {
    return { labels: [], male: [], female: [], other: [] };
  }

  const physFilter = this.descPhysicianFilter();
  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  const today = new Date();

  const labels = ['<50', '50-64', '65-74', '75+'];
  const male = [0, 0, 0, 0];
  const female = [0, 0, 0, 0];
  const other = [0, 0, 0, 0];

  for (const r of rows) {
    // Physician filter
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) continue;
    }

    // Cohort filter by Referral_Date (consistent with other KPIs)
    const rdt = parseDate(r.Referral_Date ?? null);
    if (!rdt || rdt < start || rdt > end) continue;

    // Age band
    const band = ageBandFromDob(r.DOB ?? null, today);
    if (!band) continue;

    const idx = labels.indexOf(band);
    if (idx < 0) continue;

    // Sex
    const sx = (r.Sex || '').toString().toUpperCase();
    if (sx === 'M' || sx === 'MALE') male[idx] += 1;
    else if (sx === 'F' || sx === 'FEMALE') female[idx] += 1;
    else other[idx] += 1;
  }

  return { labels, male, female, other };
});

// Wait-time distribution (ALL episodes) — includes waiting "to-date" for Scheduled + Waitlist
// Respects Demand vs Capacity filters: Physician + Start/End date (cohort by Referral_Date)
readonly waitHistData = computed(() => {
  const rows = this.rows();
  if (!rows.length) return { labels: [] as string[], counts: [] as number[] };

  const physFilter = this.descPhysicianFilter();
  const start = parseLocalDateInput(this.demandStartDate()) ?? new Date(2025, 0, 1);
  const end = parseLocalDateInput(this.demandEndDate()) ?? new Date(2025, 11, 31);
  end.setHours(23, 59, 59, 999);

  // Dashboard "as-of" anchor (per your project assumption)
  const asOf = new Date(2025, 11, 15); // Dec 15, 2025
  asOf.setHours(23, 59, 59, 999);

  const bins = [
    { label: '0–60', min: 0, max: 60 },
    { label: '61–120', min: 61, max: 120 },
    { label: '121–180', min: 121, max: 180 },
    { label: '181–365', min: 181, max: 365 },
    { label: '>365', min: 366, max: Infinity }
  ];

  const counts = bins.map(() => 0);

  for (const r of rows) {
    // Physician filter
    if (physFilter !== 'all') {
      const phys = (r.Assigned_Physician || '').trim();
      if (phys !== physFilter) continue;
    }

    // Cohort filter by Referral_Date (so waiting patients still appear)
    const rdt = parseDate(r.Referral_Date ?? null);
    if (!rdt || rdt < start || rdt > end) continue;

const rawStatus = (r.Current_Status || '').toString().toLowerCase();
const outcome = (r.Outcome || '').toString().toLowerCase();

// Exclude cancelled / deferred-type records based on raw text
const isExcluded =
  rawStatus.includes('cancel') ||
  rawStatus.includes('defer') ||
  outcome.includes('cancel') ||
  outcome.includes('defer');

if (isExcluded) continue;

    // End-of-episode date:
    // - Completed with Procedure_Date before/as-of: Referral -> Procedure
    // - Otherwise (Scheduled/Waitlist): Referral -> as-of (wait-to-date)
    const pdt = parseDate(r.Procedure_Date ?? null);
    const endStr =
      pdt && pdt <= asOf && r.Procedure_Date
        ? (r.Procedure_Date as string)
        : asOf.toISOString();

    const d = daysBetweenStr(r.Referral_Date ?? null, endStr);
    if (d == null || d < 0) continue;

    const idx = bins.findIndex(b => d >= b.min && d <= b.max);
    if (idx >= 0) counts[idx] += 1;
  }

  return { labels: bins.map(b => b.label), counts };
});

  // physician bubble: x=median wait (completed), y=active backlog, r=urgent backlog count
  readonly bubblePhysData = computed(() => {
    const rows = this.rows();
    if (!rows.length) return { labels: [] as string[], points: [] as any[], suggestedMin: 0, suggestedMax: 10 };

    type Acc = { active: number; urgentActive: number; waits: number[] };
    const byPhys: Record<string, Acc> = {};

    for (const r of rows) {
      const phys = ((r.Assigned_Physician || 'Unknown').toString().trim()) || 'Unknown';
      if (!byPhys[phys]) byPhys[phys] = { active: 0, urgentActive: 0, waits: [] };

      const s = normStatus(r.Current_Status);
      const p = normPriority(r.Priority_Level);

      if (s === 'on_waitlist' || s === 'scheduled') {
        byPhys[phys].active += 1;
        if (p === 'urgent') byPhys[phys].urgentActive += 1;
      }

      const isCompleted = s === 'completed' || (r.Outcome || '').toString().toLowerCase().includes('completed');
      if (isCompleted && r.Procedure_Date) {
        const d = daysBetweenStr(r.Referral_Date ?? null, r.Procedure_Date ?? null);
        if (d != null && d >= 0) byPhys[phys].waits.push(d);
      }
    }

    const labels = Object.keys(byPhys).sort();
    const points = labels.map(name => {
      const a = byPhys[name];
      const med = median(a.waits) ?? 0;
      return { x: med, y: a.active, r: 5 + Math.sqrt(a.urgentActive) * 4 };
    });

    const xs = points.map(p => p.x);
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length ? Math.max(...xs) : 10;

    return {
      labels,
      points,
      suggestedMin: Math.max(0, minX - 10),
      suggestedMax: maxX + 10
    };
  });

  // CHA2DS2-VASc vs wait scatter data (true score from Observation_Scores)
  readonly riskWaitScatterData = computed(() => {
    const rows = this.rows();
    if (!rows.length) return { points: [] as { x: number; y: number }[] };

    const points: { x: number; y: number }[] = [];
    const today = new Date();

    for (const r of rows) {
      const score = (r as any).CHA2DS2_VASc_Score as number | null;
      if (score == null || !Number.isFinite(score)) continue;

      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');

      const endStr = (isCompleted && r.Procedure_Date) ? (r.Procedure_Date as string) : today.toISOString();
      const waitDays = daysBetweenStr(r.Referral_Date ?? null, endStr);
      if (waitDays == null || waitDays < 0) continue;

      points.push({ x: score, y: waitDays });
    }

    return { points };
  });

  // Days to clear backlog at current throughput (kept for your right-side KPI cards)
  readonly backlogClearKpi = computed(() => {
    const rows = this.rows();
    if (!rows.length) return null;

    const backlogCount = rows.filter(r => normStatus(r.Current_Status) === 'on_waitlist').length;

    const flow = this.lineChartData();
    if (!flow.labels.length) {
      return {
        backlogCount,
        avgPerMonth: null as number | null,
        daysToClear: null as number | null,
        monthsToClear: null as number | null,
        riskBand: 'unknown' as 'good' | 'warning' | 'critical' | 'unknown',
        riskLabel: 'No data',
        barPct: 0
      };
    }

    const months = flow.labels.length || 1;
    const totalProc = flow.procedures.reduce((a, b) => a + b, 0);
    const avgPerMonth = totalProc > 0 ? totalProc / months : null;

    let daysToClear: number | null = null;
    let monthsToClear: number | null = null;

    if (backlogCount > 0 && avgPerMonth && avgPerMonth > 0) {
      daysToClear = (backlogCount / avgPerMonth) * 30;
      monthsToClear = daysToClear / 30;
    }

    let riskBand: 'good' | 'warning' | 'critical' | 'unknown' = 'unknown';
    let riskLabel = 'No data';

    if (daysToClear != null) {
      if (daysToClear <= 90) {
        riskBand = 'good';
        riskLabel = 'Within 3 months';
      } else if (daysToClear <= 180) {
        riskBand = 'warning';
        riskLabel = '3–6 months';
      } else {
        riskBand = 'critical';
        riskLabel = '> 6 months';
      }
    }

    const targetDays = 180;
    const barPct = daysToClear != null ? Math.min((daysToClear / targetDays) * 100, 100) : 0;

    return {
      backlogCount,
      avgPerMonth,
      daysToClear,
      monthsToClear,
      riskBand,
      riskLabel,
      barPct
    };
  });

  // projected backlog KPI (steady-state, simple net flow model)
  readonly backlogKpi = computed(() => {
    const rows = this.rows();
    if (!rows.length) return null;

    const active = rows.filter(r => {
      const s = normStatus(r.Current_Status);
      return s === 'on_waitlist' || s === 'scheduled';
    }).length;

    const flow = this.lineChartData();
    if (!flow.labels.length) {
      return {
        current: active,
        direction: 'flat' as 'up' | 'down' | 'flat',
        netPerMonth: 0,
        after3: active,
        after6: active,
        after12: active
      };
    }

    const months = flow.labels.length || 1;
    const totalRef = flow.referrals.reduce((a, b) => a + b, 0);
    const totalProc = flow.procedures.reduce((a, b) => a + b, 0);

    const avgRef = totalRef / months;
    const avgProc = totalProc / months;
    const netPerMonth = avgRef - avgProc;

    const project = (m: number) => Math.max(0, Math.round(active + netPerMonth * m));

    let direction: 'up' | 'down' | 'flat' = 'flat';
    if (netPerMonth > 0.5) direction = 'up';
    else if (netPerMonth < -0.5) direction = 'down';

    return {
      current: active,
      direction,
      netPerMonth,
      after3: project(3),
      after6: project(6),
      after12: project(12)
    };
  });

  // urgent conversion risk KPI (urgent cases over 30-day target)
  readonly urgentRiskKpi = computed(() => {
    const rows = this.rows();
    if (!rows.length) return null;

    const threshold = 30;
    const today = new Date();

    const urgentRows = rows.filter(r => normPriority(r.Priority_Level) === 'urgent');

    if (!urgentRows.length) {
      return {
        total: 0,
        over: 0,
        pctOver: 0,
        avgWait: null as number | null,
        threshold
      };
    }

    const waits: number[] = [];
    let over = 0;

    for (const r of urgentRows) {
      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');

      const endStr = (isCompleted && r.Procedure_Date) ? (r.Procedure_Date as string) : today.toISOString();

      const d = daysBetweenStr(r.Referral_Date ?? null, endStr);
      if (d == null) continue;

      waits.push(d);
      if (d > threshold) over += 1;
    }

    const total = waits.length;
    if (!total) {
      return {
        total: 0,
        over: 0,
        pctOver: 0,
        avgWait: null as number | null,
        threshold
      };
    }

    const avgWait = waits.reduce((acc, v) => acc + v, 0) / total;
    const pctOver = (over / total) * 100;

    return {
      total,
      over,
      pctOver,
      avgWait,
      threshold
    };
  });

  // schedule grid (gantt-ish): completed procedures per physician per month
  readonly scheduleGridData = computed(() => {
    const rows = this.rows();
    if (!rows.length) {
      return {
        monthLabels: [] as string[],
        physicians: [] as string[],
        grid: [] as number[][]
      };
    }

    const monthSet = new Set<string>();
    const physSet = new Set<string>();

    const counts: Record<string, Record<string, number>> = {}; // phys -> monthKey -> count

    for (const r of rows) {
      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');
      if (!isCompleted || !r.Procedure_Date) continue;

      const mk = monthKey(r.Procedure_Date ?? null);
      if (!mk) continue;

      const phys = ((r.Assigned_Physician || 'Unknown').toString().trim()) || 'Unknown';

      monthSet.add(mk);
      physSet.add(phys);

      if (!counts[phys]) counts[phys] = {};
      counts[phys][mk] = (counts[phys][mk] ?? 0) + 1;
    }

    const monthKeys = Array.from(monthSet).sort();
    const monthLabels = monthKeys.map(monthLabel);
    const physicians = Array.from(physSet).sort();

    const grid = physicians.map(p => monthKeys.map(mk => counts[p]?.[mk] ?? 0));

    return { monthLabels, physicians, grid };
  });

  // ----- chart handles & state ---------------------------------------------
  @ViewChild('episodesChart') episodesChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('waitPriorityChart') waitPriorityChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('backlogChart') backlogChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('ageSexChart') ageSexChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('waitHistChart') waitHistChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('bubblePhysChart') bubblePhysChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('riskScatterChart') riskScatterChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('edSparkChart') edSparkChart?: ElementRef<HTMLCanvasElement>;
  @ViewChild('acuteSparkChart') acuteSparkChart?: ElementRef<HTMLCanvasElement>;  

  private episodesChartInstance: Chart | null = null;
  private waitPriorityChartInstance: Chart | null = null;
  private backlogChartInstance: Chart | null = null;
  private ageSexChartInstance: Chart | null = null;
  private waitHistChartInstance: Chart | null = null;
  private bubblePhysChartInstance: Chart | null = null;
  private riskScatterChartInstance: Chart | null = null;
  private edSparkChartInstance: Chart | null = null;
  private acuteSparkChartInstance: Chart | null = null;

  // map state
  private map: L.Map | null = null;
  private layer: L.LayerGroup | null = null;
  private mapReady = false;
  private viewInitialized = false;

  constructor(private readonly dataService: DataService) {
    // update clusters when filtered rows change
    effect(() => {
      const rows = this.filteredRows();
      if (!this.mapReady || !this.map || !this.layer) return;
      if (!rows.length) {
        this.layer.clearLayers();
        return;
      }
      this.renderPatientClusters(rows);
    });


// ED sparkline (real monthly encounters)
effect(() => {
  const mode = this.viewMode();
  if (!this.viewInitialized) return;
  if (mode !== 'descriptive') return;
  if (!this.edSparkChart) return;

  const s = this.edSpark();
  const ctx = this.edSparkChart.nativeElement.getContext('2d');
  if (!ctx) return;

  if (!this.edSparkChartInstance) {
    this.edSparkChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: s.labels,
        datasets: [{
          data: s.values,
          borderColor: '#3ea6ff',
          backgroundColor: 'rgba(62,166,255,0.15)',
          pointRadius: 0,
          tension: 0.35,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } }
      }
    });
  } else {
    this.edSparkChartInstance.data.labels = s.labels;
    (this.edSparkChartInstance.data.datasets[0].data as number[]) = s.values;
    this.edSparkChartInstance.update();
  }
});

// Acute sparkline (real monthly encounters)
effect(() => {
  const mode = this.viewMode();
  if (!this.viewInitialized) return;
  if (mode !== 'descriptive') return;
  if (!this.acuteSparkChart) return;

  const s = this.acuteSpark();
  const ctx = this.acuteSparkChart.nativeElement.getContext('2d');
  if (!ctx) return;

  if (!this.acuteSparkChartInstance) {
    this.acuteSparkChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: s.labels,
        datasets: [{
          data: s.values,
          borderColor: '#f5a623',
          backgroundColor: 'rgba(245,166,35,0.15)',
          pointRadius: 0,
          tension: 0.35,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } }
      }
    });
  } else {
    this.acuteSparkChartInstance.data.labels = s.labels;
    (this.acuteSparkChartInstance.data.datasets[0].data as number[]) = s.values;
    this.acuteSparkChartInstance.update();
  }
});

    // demand vs capacity line chart
    effect(() => {
      const data = this.lineChartData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.episodesChart) return;
      if (mode !== 'descriptive') return;
      if (!data.labels.length) return;

      const ctx = this.episodesChart.nativeElement.getContext('2d');
      if (!ctx) return;

      if (!this.episodesChartInstance) {
        const config: ChartConfiguration<'line'> = {
          type: 'line',
          data: {
            labels: data.labels,
            datasets: [
              {
                label: 'Referrals (demand)',
                data: data.referrals,
                borderColor: '#3ea6ff',
                backgroundColor: 'rgba(62,166,255,0.22)',
                pointBackgroundColor: '#3ea6ff',
                pointBorderColor: '#3ea6ff',
                tension: 0.3,
                fill: true
              },
              {
                label: 'Completed procedures (capacity)',
                data: data.procedures,
                borderColor: '#2ecc71',
                backgroundColor: 'rgba(46,204,113,0.22)',
                pointBackgroundColor: '#2ecc71',
                pointBorderColor: '#2ecc71',
                tension: 0.3,
                fill: true
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Patients' } },
              x: { title: { display: true, text: 'Month' } }
            },
            plugins: { legend: { display: true, position: 'bottom' } }
          }
        };
        this.episodesChartInstance = new Chart(ctx, config);
      } else {
        this.episodesChartInstance.data.labels = data.labels;
        const ds = this.episodesChartInstance.data.datasets;
        if (ds[0]) (ds[0].data as number[]) = data.referrals;
        if (ds[1]) (ds[1].data as number[]) = data.procedures;
        this.episodesChartInstance.update();
      }

      // ED sparkline
effect(() => {
  const mode = this.viewMode();
  if (!this.viewInitialized) return;
  if (mode !== 'descriptive') return;
  if (!this.edSparkChart) return;

  const s = this.edSpark();
  const ctx = this.edSparkChart.nativeElement.getContext('2d');
  if (!ctx) return;

  if (!this.edSparkChartInstance) {
    this.edSparkChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: s.labels,
        datasets: [{
          data: s.values,
          borderColor: '#3ea6ff',
          backgroundColor: 'rgba(62,166,255,0.15)',
          pointRadius: 0,
          tension: 0.35,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } }
      }
    });
  } else {
    this.edSparkChartInstance.data.labels = s.labels;
    (this.edSparkChartInstance.data.datasets[0].data as number[]) = s.values;
    this.edSparkChartInstance.update();
  }
});

// Acute sparkline
effect(() => {
  const mode = this.viewMode();
  if (!this.viewInitialized) return;
  if (mode !== 'descriptive') return;
  if (!this.acuteSparkChart) return;

  const s = this.acuteSpark();
  const ctx = this.acuteSparkChart.nativeElement.getContext('2d');
  if (!ctx) return;

  if (!this.acuteSparkChartInstance) {
    this.acuteSparkChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: s.labels,
        datasets: [{
          data: s.values,
          borderColor: '#f5a623',
          backgroundColor: 'rgba(245,166,35,0.15)',
          pointRadius: 0,
          tension: 0.35,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } }
      }
    });
  } else {
    this.acuteSparkChartInstance.data.labels = s.labels;
    (this.acuteSparkChartInstance.data.datasets[0].data as number[]) = s.values;
    this.acuteSparkChartInstance.update();
  }
});
    });

    // Average wait by priority bar
    effect(() => {
      const data = this.waitByPriorityData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.waitPriorityChart) return;
      if (mode !== 'descriptive') return;
      if (!data.labels.length) return;

      const ctx = this.waitPriorityChart.nativeElement.getContext('2d');
      if (!ctx) return;

      // Match backlog chart colors
      const priorityColor = (label: string) => {
        const v = (label || '').toLowerCase().trim();
        if (v === 'elective') return '#3ea6ff'; // blue
        if (v === 'priority') return '#f5a623'; // orange
        if (v === 'urgent') return '#ff6b6b';   // red
        return '#9fb2c6';                       // fallback grey
      };

      const barColors = data.labels.map(priorityColor);

      if (!this.waitPriorityChartInstance) {
        const config: ChartConfiguration<'bar'> = {
          type: 'bar',
          data: {
            labels: data.labels,
            datasets: [
              {
                data: data.values,
                label: 'Average wait to date (days)',
                backgroundColor: barColors
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, title: { display: true, text: 'Days' } },
              x: { title: { display: true, text: 'Priority level' } }
            },
            plugins: { legend: { display: false } }
          }
        };
        this.waitPriorityChartInstance = new Chart(ctx, config);
      } else {
        this.waitPriorityChartInstance.data.labels = data.labels;

        const ds0 = this.waitPriorityChartInstance.data.datasets[0];
        (ds0.data as number[]) = data.values;

        // IMPORTANT: update colors too when labels change
        (ds0.backgroundColor as any) = barColors;

        this.waitPriorityChartInstance.update();
      }
    });

    // backlog stacked bar
    effect(() => {
      const data = this.backlogByPhysPriorityData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.backlogChart) return;
      if (mode !== 'descriptive') return;
      if (!data.physicians.length || !data.priorities.length) return;

      const ctx = this.backlogChart.nativeElement.getContext('2d');
      if (!ctx) return;

      const colorPalette = ['#3ea6ff', '#f5a623', '#ff6b6b', '#9b59b6', '#2ecc71'];

      const datasets = data.priorities.map((pri, idx) => ({
        label: pri,
        data: data.physicians.map((_, pIdx) => data.matrix[idx][pIdx]),
        backgroundColor: colorPalette[idx % colorPalette.length]
      }));

      if (!this.backlogChartInstance) {
        const config: ChartConfiguration<'bar'> = {
          type: 'bar',
          data: { labels: data.physicians, datasets: datasets as any },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true, title: { display: true, text: 'Physician' } },
              y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Patients' } }
            },
            plugins: { legend: { display: false} }
          }
        };
        this.backlogChartInstance = new Chart(ctx, config);
      } else {
        this.backlogChartInstance.data.labels = data.physicians;
        this.backlogChartInstance.data.datasets = datasets as any;
        this.backlogChartInstance.update();
      }
    });

    // age x sex stacked bar
    effect(() => {
      const data = this.ageSexData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.ageSexChart) return;
      if (mode !== 'descriptive') return;
      if (!data.labels.length) return;

      const ctx = this.ageSexChart.nativeElement.getContext('2d');
      if (!ctx) return;

      if (!this.ageSexChartInstance) {
        const config: ChartConfiguration<'bar'> = {
          type: 'bar',
          data: {
            labels: data.labels,
            datasets: [
              { label: 'Male', data: data.male, backgroundColor: '#3ea6ff' },
              { label: 'Female', data: data.female, backgroundColor: '#ff6b6b' },
              { label: 'Other/Unknown', data: data.other, backgroundColor: '#9b59b6' }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true, title: { display: true, text: 'Age' } },
              y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Patients' } }
            },
            plugins: { legend: { display: true, position: 'bottom' } }
          }
        };
        this.ageSexChartInstance = new Chart(ctx, config);
      } else {
        this.ageSexChartInstance.data.labels = data.labels;
        (this.ageSexChartInstance.data.datasets[0].data as number[]) = data.male;
        (this.ageSexChartInstance.data.datasets[1].data as number[]) = data.female;
        (this.ageSexChartInstance.data.datasets[2].data as number[]) = data.other;
        this.ageSexChartInstance.update();
      }
    });

    // wait histogram
    effect(() => {
      const data = this.waitHistData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.waitHistChart) return;
      if (mode !== 'descriptive') return;
      if (!data.labels.length) return;

      const ctx = this.waitHistChart.nativeElement.getContext('2d');
      if (!ctx) return;

      if (!this.waitHistChartInstance) {
        const config: ChartConfiguration<'bar'> = {
          type: 'bar',
          data: {
            labels: data.labels,
            datasets: [{ label: 'Episodes', data: data.counts, backgroundColor: '#3ea6ff' }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Patients' } },
              x: { title: { display: true, text: 'Days' } }
            },
            plugins: { legend: { display: false } }
          }
        };
        this.waitHistChartInstance = new Chart(ctx, config);
      } else {
        this.waitHistChartInstance.data.labels = data.labels;
        (this.waitHistChartInstance.data.datasets[0].data as number[]) = data.counts;
        this.waitHistChartInstance.update();
      }
    });

    // bubble chart
    effect(() => {
      const data = this.bubblePhysData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.bubblePhysChart) return;
      if (mode !== 'descriptive') return;
      if (!data.points.length) return;

      const ctx = this.bubblePhysChart.nativeElement.getContext('2d');
      if (!ctx) return;

      const datasetPoints = data.points.map(p => ({ x: p.x, y: p.y, r: p.r }));

      if (!this.bubblePhysChartInstance) {
        const config: ChartConfiguration<'bubble'> = {
          type: 'bubble',
          data: {
            labels: data.labels,
            datasets: [
              {
                label: 'Physician',
                data: datasetPoints,
                backgroundColor: 'rgba(62,166,255,0.55)',
                borderColor: '#3ea6ff'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: 'Median wait (days)' }, suggestedMin: data.suggestedMin, suggestedMax: data.suggestedMax },
              y: { title: { display: true, text: 'Active backlog (episodes)' }, beginAtZero: true }
            },
            plugins: { legend: { display: false } }
          }
        };
        this.bubblePhysChartInstance = new Chart(ctx, config);
      } else {
        (this.bubblePhysChartInstance.data.datasets[0].data as any) = datasetPoints;
        const opts: any = this.bubblePhysChartInstance.options;
        if (opts.scales?.x) {
          opts.scales.x.suggestedMin = data.suggestedMin;
          opts.scales.x.suggestedMax = data.suggestedMax;
        }
        this.bubblePhysChartInstance.update();
      }
    });

    // risk scatter
    effect(() => {
      const data = this.riskWaitScatterData();
      const mode = this.viewMode();

      if (!this.viewInitialized) return;
      if (!this.riskScatterChart) return;
      if (mode !== 'descriptive') return;
      if (!data.points.length) return;

      const ctx = this.riskScatterChart.nativeElement.getContext('2d');
      if (!ctx) return;

      const datasetPoints = data.points.map(p => ({ x: p.x, y: p.y }));

      if (!this.riskScatterChartInstance) {
        const config: ChartConfiguration<'scatter'> = {
          type: 'scatter',
          data: {
            datasets: [
              { label: 'Episodes', data: datasetPoints, pointRadius: 3, pointBackgroundColor: '#3ea6ff' }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: 'CHA₂DS₂-VASc score' }, beginAtZero: true, ticks: { stepSize: 1 } },
              y: { title: { display: true, text: 'Wait time (days)' }, beginAtZero: true }
            },
            plugins: { legend: { display: false } }
          }
        };
        this.riskScatterChartInstance = new Chart(ctx, config);
      } else {
        const ds = this.riskScatterChartInstance.data.datasets[0];
        (ds.data as any) = datasetPoints;
        this.riskScatterChartInstance.update();
      }
    });
  }

  // lifecycle
async ngOnInit(): Promise<void> {
  try {
    const [rows, enc] = await Promise.all([
      this.dataService.getFlatRows(),
      this.dataService.getEncounterRows()
    ]);

    this.rows.set(rows);
    this.encounters.set(enc);
  } catch (err) {
    console.error(err);
    this.error.set('Failed to load waitlist dataset.');
  } finally {
    this.loading.set(false);
  }
}

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.initMap();
    this.mapReady = true;

    const rows = this.filteredRows();
    if (rows.length) this.renderPatientClusters(rows);
  }

  // view + filter setters
  setViewMode(mode: 'map' | 'descriptive' | 'schedule'): void {
    this.viewMode.set(mode);
  }

  setPriorityFilter(value: string): void {
    this.priorityFilter.set(value as any);
  }

  setAgeBandFilter(value: string): void {
    this.ageBandFilter.set(value as any);
  }

  setSexFilter(value: string): void {
    this.sexFilter.set(value as any);
  }

  setFsaFilter(value: string): void {
    this.fsaFilter.set(value === 'all' ? 'all' : value);
  }

  setDescPhysicianFilter(value: string): void {
    this.descPhysicianFilter.set(value === 'all' ? 'all' : value);
  }

  setDescDxFilter(value: string): void {
    this.descDxFilter.set(value === 'all' ? 'all' : value);
  }

  setDescComorbFilter(value: string): void {
    this.descComorbFilter.set(value as any);
  }

// ===================== Schedule Worklist (CLEAN) =====================

// filters
readonly schedStatus = signal<'all' | 'on_waitlist' | 'scheduled'>('all');
readonly schedPriority = signal<'all' | 'urgent' | 'priority' | 'elective'>('all');
readonly schedPhysician = signal<string>('all');
readonly schedSearch = signal<string>('');

// filter setters
setSchedStatus(v: string): void {
  this.schedStatus.set((v || 'scheduled') as any);
}
setSchedPriority(v: string): void {
  this.schedPriority.set((v || 'all') as any);
}
setSchedPhysician(v: string): void {
  this.schedPhysician.set(v || 'all');
}
setSchedSearch(v: string): void {
  this.schedSearch.set(v || '');
}

// physician dropdown
readonly schedulePhysicianOptions = computed<string[]>(() => {
  const set = new Set<string>();
  for (const r of this.rows()) {
    const s = normStatus(r.Current_Status);
    if (s === 'scheduled' || s === 'on_waitlist') {
      set.add((r.Assigned_Physician || 'Unknown').toString());
    }
  }
  return Array.from(set).sort();
});

// minimal worklist (NO sorting yet)
readonly scheduleWorklist = computed(() => {
  const base = this.rows().filter(r => {
    const s = normStatus(r.Current_Status);
    if (this.schedStatus() !== 'all' && s !== this.schedStatus()) return false;
    if (this.schedPriority() !== 'all' && normPriority(r.Priority_Level) !== this.schedPriority()) return false;
    if (this.schedPhysician() !== 'all' && r.Assigned_Physician !== this.schedPhysician()) return false;

    const q = this.schedSearch().toLowerCase();
    if (q) {
      const pid = (r.Patient_ID || '').toLowerCase();
      const rid = ((r as any).Referral_ID || '').toLowerCase();
      if (!pid.includes(q) && !rid.includes(q)) return false;
    }

    return s === 'scheduled' || s === 'on_waitlist';
  });

const key = this.worklistSortKey();
const dir = this.worklistSortDir();

if (!key) return base; // ✅ unsorted: return original order

const getVal = (r: FlatRow): any => {
  switch (key) {
    case 'patient': return (r.Patient_ID || '');
    case 'age': return this.getAge(r) ?? -1;
    case 'sex': return (r.Sex || '');
    case 'status': return normStatus(r.Current_Status);
    case 'priority': return normPriority(r.Priority_Level);
    case 'physician': return (r.Assigned_Physician || '');
    case 'referral': return parseDate(r.Referral_Date ?? null)?.getTime() ?? -1;
    case 'scheduled': return parseDate((r as any).Scheduled_Date ?? null)?.getTime() ?? -1;
    case 'days_waiting': return this.getDaysWaiting(r) ?? -1;
    case 'cha2': return this.getCha2(r) ?? -1;
    case 'comorb': return this.getComorbCount(r) ?? -1;
    case 'ed': return this.getEdVisits(r) ?? -1;
    case 'acute': return this.getAcuteCases(r) ?? -1;
    default: return 0;
  }
};

return [...base].sort((a, b) => {
  const av = getVal(a);
  const bv = getVal(b);

  if (typeof av === 'number' && typeof bv === 'number') {
    return dir === 'asc' ? av - bv : bv - av;
  }

  const as = (av ?? '').toString();
  const bs = (bv ?? '').toString();
  return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
});
});

// --- Info popovers (click "i") ---
readonly openInfoKey = signal<string | null>(null);

toggleInfo(key: string): void {
  this.openInfoKey.set(this.openInfoKey() === key ? null : key);
}

closeInfo(): void {
  this.openInfoKey.set(null);
}

// Plain-language help text for every KPI / box
readonly infoText: Record<string, string> = {
  avgWait1: 'Average days from referral received to being added to the waitlist (intake/triage time).',
  avgWait2: 'Average days from being waitlisted to procedure completion (completed cases only).',
  avgTotalWait: 'Average days from referral to today (if still waiting) or to procedure date (if completed).',

  completed: 'Number of ablation procedures completed in the dataset.',
  onWaitlist: 'Number of patients currently waiting without a completed procedure.',
  scheduled: 'Number of patients with a scheduled procedure date (not yet completed).',

  demandCapacity: 'Monthly referrals (demand) compared to monthly completed procedures (capacity).',
  avgWaitPriority: 'Average wait-to-date from referral to today/procedure, grouped by priority.',
  waitlistedByPhys: 'Current waiting patients (not completed) by assigned physician and priority.',
  ageSex: 'Age band and sex breakdown of referred patients in the selected timeframe.',
  waitDist: 'Distribution of wait times (referral to procedure) for completed cases.',

  qol: 'Average AFEQT score before and after ablation; higher scores mean better quality of life.',
  projectedBacklog: 'Expected change in backlog if average monthly demand and capacity continue.',
  los: 'Total hospital bed-days used by admitted patients and average length of stay.',
  ed: 'Total emergency visits for AFib and how they vary by month.',
  acute: 'Total hospital admissions and the share of patients admitted at least once.'
};

  // --- KPI tooltip hover (250ms delay) ---
hoveredKpi = signal<string | null>(null);
private kpiHoverTimer: any = null;

onKpiEnter(key: string): void {
  if (this.kpiHoverTimer) clearTimeout(this.kpiHoverTimer);
  this.kpiHoverTimer = setTimeout(() => {
    this.hoveredKpi.set(key);
  }, 250);
}

onKpiLeave(): void {
  if (this.kpiHoverTimer) clearTimeout(this.kpiHoverTimer);
  this.kpiHoverTimer = null;
  this.hoveredKpi.set(null);
}

readonly kpiHelpText: Record<string, string> = {
  avgWait1: 'Average days from Referral Date to Waitlisted Date (Referral → Waitlist entry). Measures intake/triage processing time.',
  avgWait2: 'Average days from Waitlisted Date to Procedure Completed (Waitlist entry → Completed). Completed cases only.',
  avgTotalWait: 'Average days from Referral Date to end-of-episode. Completed: Referral → Procedure. Not completed: Referral → today (wait-to-date).',
  completed: 'Total number of ablation procedures completed in the selected cohort/time window.',
  onWaitlist: 'Total patients currently in status “On Waitlist” (not yet scheduled or completed).',
  scheduled: 'Total patients currently in status “Scheduled” (has a scheduled date, not yet completed).'
};

  // ------------------------ Map rendering -----------------------------------

  private initMap(): void {
    if (this.map) return;

    const container = document.getElementById('patient-map');
    if (!container) return;

    this.map = L.map(container, {
      center: [OTTAWA_CENTER_LAT, OTTAWA_CENTER_LNG],
      zoom: 9,
      scrollWheelZoom: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    this.layer = L.layerGroup().addTo(this.map);
  }

  private renderPatientClusters(rows: FlatRow[]): void {
    if (!this.map || !this.layer) return;
    this.layer.clearLayers();
    const bounds = L.latLngBounds([]);

    type FsaMetrics = { count: number; waits: number[]; urgentCount: number };
    const metrics: Record<string, FsaMetrics> = {};

    for (const r of rows) {
      const fsa = (r.Postal_Code || '').slice(0, 3).toUpperCase();
      if (!fsa) continue;

      if (!metrics[fsa]) metrics[fsa] = { count: 0, waits: [], urgentCount: 0 };
      const m = metrics[fsa];

      m.count += 1;

      const status = normStatus(r.Current_Status);
      const outcome = (r.Outcome || '').toString().toLowerCase();
      const isCompleted = status === 'completed' || outcome.includes('completed');

      if (isCompleted && r.Procedure_Date) {
        const d = daysBetweenStr(r.Referral_Date ?? null, r.Procedure_Date ?? null);
        if (d != null) m.waits.push(d);
      }

      if (normPriority(r.Priority_Level) === 'urgent') m.urgentCount += 1;
    }

    Object.entries(metrics).forEach(([fsa, m]) => {
      const { lat, lng } = pseudoFsaCentroid(fsa);
      const medianWaitVal = median(m.waits);
      const urgentPct = m.count ? (m.urgentCount / m.count) * 100 : null;

      const radius = 6 + Math.sqrt(m.count) * 3;

      const popupHtml = `
        <div style="font-family: system-ui; font-size: 12px;">
          <b>FSA:</b> ${fsa}<br/>
          <b>Patients:</b> ${m.count}<br/>
          <b>Median wait (completed):</b> ${medianWaitVal != null ? `${medianWaitVal.toFixed(0)} d` : '—'}<br/>
          <b>Urgent share:</b> ${urgentPct != null ? `${urgentPct.toFixed(0)}%` : '—'}
        </div>
      `;

const marker = L.circleMarker([lat, lng], {
  radius,
  color: '#000000',        // black outline (key change)
  weight: 2,
  fillColor: '#00e676',    // neon green (high contrast)
  fillOpacity: 0.55
}).bindPopup(popupHtml);

      marker.addTo(this.layer!);
    });
  }
}
